<script setup lang="ts">
import { computed } from 'vue';
import { DgrIcon, DgrLoading } from '@stockmarkteam/donguri-ui';
import { FeedSummaryGenerationState } from '@/types';

const props = defineProps<{
  state: FeedSummaryGenerationState;
  summaryTime?: string;
}>();

defineEmits<{
  click: [e: MouseEvent];
}>();

const disabled = computed(
  () => props.state !== 'ready' && props.state !== 'ready-stale',
);

const isLoading = computed(() => props.state === 'processing');

const formattedSummaryTime = computed(() =>
  props.summaryTime ? props.summaryTime.replaceAll(/[()]/g, '') : undefined,
);
const readyMessage = computed(() =>
  formattedSummaryTime.value
    ? `${formattedSummaryTime.value}を生成済み`
    : '生成済み',
);

const buttonLabel = computed(() => {
  switch (props.state) {
    case 'ready':
    case 'ready-stale':
    case 'disabled':
    case 'no-articles':
      return '主要トピックをまとめる';
    case 'processing':
      return '主要トピックを生成中';
    case 'no-update':
      return '主要トピックを生成済み';
    default:
      return '';
  }
});

const tooltipText = computed(() => {
  if (props.state === 'no-update' || props.state === 'no-articles') {
    return '最新の内容です。おすすめ情報の内容が更新されると、AIで主要トピックをまとめられます。';
  }
  if (props.state === 'processing') {
    return '主要トピックを作成中です';
  }
  if (props.state === 'ready-stale') {
    return 'おすすめ情報の内容が更新されました。AIで主要トピックをまとめられます。';
  }
  return undefined;
});
</script>

<template>
  <div v-if="state === 'no-update'" class="c-title-medium--m no-update">
    {{ readyMessage }}
  </div>
  <button
    v-else-if="state === 'ready-stale'"
    type="button"
    class="summary c-btn c-btn--auto c-btn--small c-btn--AnewsPrimary"
    :class="{ disabled, loading: isLoading }"
    :disabled
    @click="$emit('click', $event)"
    v-tooltip="{
      content: tooltipText,
      theme: 'small',
    }"
  >
    <DgrIcon name="sync" size="small" :keep-fill="false" class="sync" />
    最新の関心で再生成
  </button>
  <button
    v-else
    type="button"
    class="summary c-btn c-btn--auto c-btn--small c-btn--AnewsPrimary"
    :class="{ disabled, loading: isLoading }"
    :disabled
    @click="$emit('click', $event)"
    v-tooltip="{
      content: tooltipText,
      theme: 'small',
    }"
  >
    <DgrLoading
      v-if="isLoading"
      size="small"
      :keep-fill="false"
      class="loading-icon"
    />
    <DgrIcon
      v-else
      name="sparkles-fill"
      size="small"
      :keep-fill="false"
      class="sparkles"
    />
    {{ buttonLabel }}
  </button>
</template>
<style scoped lang="scss">
.summary {
  gap: 4px;
  text-wrap: nowrap;

  &.loading,
  &.loading:hover {
    // 生成中は disabled でもあるため、donguri の disabled 文字色（gray600）が緑背景に乗らないよう白を維持する
    color: $color-white;
    background: linear-gradient(
      90deg,
      $color-green600 0%,
      $color-green600 40%,
      $color-green400 50%,
      $color-green600 60%,
      $color-green600 100%
    );
    background-size: 200% 100%;
    animation: shimmer 2.5s linear infinite;
  }
}

.no-update {
  color: $color-gray800;
}

@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}
</style>
